import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, TextField, Button, Grid, Alert } from "@mui/material";
import { Save } from "lucide-react";
import Swal from "sweetalert2";
import { getRevenueSettings, updateRevenueSettings } from "../../../redux/actions/revenueAction";
import RouteHeader from "../../../components/common/RouteHeader";

const RevenueSettings = () => {
    const dispatch = useDispatch();
    const { revenueSettings } = useSelector(state => state.revenueReducer);

    const [superAdmin, setSuperAdmin] = useState("");
    const [admin, setAdmin] = useState("");
    const [partner, setPartner] = useState("");
    const [affiliate, setAffiliate] = useState("");

    useEffect(() => {
        dispatch(getRevenueSettings());
    }, [dispatch]);

    useEffect(() => {
        if (revenueSettings) {
            setSuperAdmin(String(revenueSettings.super_admin_percentage ?? 19));
            setAdmin(String(revenueSettings.admin_percentage ?? 18));
            setPartner(String(revenueSettings.partner_percentage ?? 53));
            setAffiliate(String(revenueSettings.affiliate_percentage ?? 10));
        }
    }, [revenueSettings]);

    const total = (parseFloat(superAdmin) || 0) + (parseFloat(admin) || 0) + (parseFloat(partner) || 0) + (parseFloat(affiliate) || 0);
    const isValid = Math.abs(total - 100) < 0.01;

    const handleSave = () => {
        if (!isValid) {
            Swal.fire({ icon: "warning", title: "Invalid Split", text: `Total must be 100%. Current total: ${total.toFixed(2)}%` });
            return;
        }
        dispatch(updateRevenueSettings({
            data: {
                super_admin_percentage: parseFloat(superAdmin),
                admin_percentage: parseFloat(admin),
                partner_percentage: parseFloat(partner),
                affiliate_percentage: parseFloat(affiliate),
            }
        }));
    };

    const fieldStyle = { background: "#fff", borderRadius: "8px" };

    return (
        <Box sx={{ p: 2 }}>
            <RouteHeader title="Revenue Settings" subtitle="Configure revenue split percentages" />

            <Box sx={{ background: "#fff", borderRadius: "12px", p: 3, boxShadow: "0 1px 6px rgba(0,0,0,0.07)", maxWidth: 600 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 2, color: "#1e293b" }}>Split Percentages</Typography>

                {!isValid && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Total must equal 100%. Current total: <strong>{total.toFixed(2)}%</strong>
                    </Alert>
                )}

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Super Admin (%)" type="number" value={superAdmin} onChange={e => setSuperAdmin(e.target.value)} size="small" sx={fieldStyle}
                            inputProps={{ min: 0, max: 100, step: 0.01 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Admin (%)" type="number" value={admin} onChange={e => setAdmin(e.target.value)} size="small" sx={fieldStyle}
                            inputProps={{ min: 0, max: 100, step: 0.01 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Partner (%)" type="number" value={partner} onChange={e => setPartner(e.target.value)} size="small" sx={fieldStyle}
                            inputProps={{ min: 0, max: 100, step: 0.01 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Affiliate (%)" type="number" value={affiliate} onChange={e => setAffiliate(e.target.value)} size="small" sx={fieldStyle}
                            inputProps={{ min: 0, max: 100, step: 0.01 }} />
                    </Grid>
                </Grid>

                <Box sx={{ mt: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography sx={{ fontSize: 14, color: isValid ? "#22c55e" : "#ef4444", fontWeight: 600 }}>
                        Total: {total.toFixed(2)}%
                    </Typography>
                    <Button variant="contained" onClick={handleSave} disabled={!isValid} startIcon={<Save size={16} />}
                        sx={{ textTransform: "none", background: "linear-gradient(135deg, #6366f1, #8b5cf6)", "&:hover": { background: "linear-gradient(135deg, #4f46e5, #7c3aed)" }, "&.Mui-disabled": { background: "#e2e8f0" } }}>
                        Save Settings
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default RevenueSettings;
